import axios from "axios";
import config from "config/config";
import { normalizeFilename } from "shared/utils";
import type {
  LandXSurvey,
  LandXPatchAnswerPayload,
  LandXPatchAnswerResponse,
  LandXSubmitPayload
} from "./land-x.types";

// Unauthenticated instance – survey respondents are not logged in
const landXInstance = axios.create({
  baseURL: config.apiHost,
  timeout: 60000
});

const BASE = "/portal/v1/premium_rental_history/surveys";

export const landXApi = {
  /** Get a presigned S3 URL for file upload (unauthenticated) */
  getPresignedUrl: async (token: string, file: File): Promise<string> => {
    const queryParams = new URLSearchParams({
      filename: normalizeFilename(file.name),
      type: file.type
    }).toString();
    const { data } = await landXInstance.get<{ url: string }>(
      `${BASE}/${token}/presign?${queryParams}`
    );
    return data.url;
  },

  /** GET survey by token */
  getSurvey: async (
    token: string,
    boomFillOutToken?: string
  ): Promise<LandXSurvey> => {
    const { data } = await landXInstance.get<LandXSurvey>(`${BASE}/${token}`, {
      params: boomFillOutToken
        ? { boom_fill_out_token: boomFillOutToken }
        : undefined
    });
    return data;
  },

  /** Patch a single answer (fire-and-forget from the UI side) */
  patchAnswer: async (
    token: string,
    answerId: number,
    payload: LandXPatchAnswerPayload
  ): Promise<LandXPatchAnswerResponse> => {
    const { data } = await landXInstance.post<LandXPatchAnswerResponse>(
      `${BASE}/${token}/answers/${answerId}/update`,
      payload
    );
    return data;
  },

  /** Decline survey */
  declineSurvey: async (
    token: string,
    decline_reason: string,
    // BSCPF-6584. Employment surveys send one of four fixed reason codes; the free text
    // behind "other" rides alongside it. Rental history keeps sending prose as the reason.
    decline_reason_details?: string
  ): Promise<LandXSurvey> => {
    const { data } = await landXInstance.post<LandXSurvey>(
      `${BASE}/${token}/decline`,
      {
        decline_reason,
        ...(decline_reason_details ? { decline_reason_details } : {})
      }
    );
    return data;
  },

  /** Submit all answers at once (wait for response) */
  submitSurvey: async (
    token: string,
    payload: LandXSubmitPayload
  ): Promise<LandXSurvey> => {
    const { data } = await landXInstance.post<LandXSurvey>(
      `${BASE}/${token}/submit`,
      payload
    );
    return data;
  }
};
